import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { useBlocker } from 'react-router';
import { ANLAM } from '../theme';

/**
 * KAYDEDİLMEMİŞ İÇERİKTEN ÇIKIŞ KORUMASI (yüzey dürüstlüğü turu — madde 221;
 * Kapsam §20.3b P12 · Teknoloji v1.19 §1).
 *
 * > **Tek cümle: KORUMA YÖNLENDİRİCİDE TEK NOKTADADIR — EKRAN KURAL BİLMEZ,
 * > YALNIZ SAYI BİLDİRİR.**
 *
 * ⚠️ **NEDEN TEK NOKTA:** yönlendirici aynı anda **tek engelleyici** destekler
 * (`useBlocker`); iki ekran kendi engelleyicisini kursaydı biri ötekini sessizce
 * ezerdi. Ekran `useKaydedilmemisIcerik(sayi, etiket)` ile kaç kaydedilmemiş
 * kalemi olduğunu bildirir; karar (sor · sorma) burada verilir.
 *
 * Üç kural, üçü de kolay delinir:
 *
 * 1. ⚠️ **AYNI SAYFA İÇİNDE SORU YOK** (P1-c revizyonu): yalnız YOL (pathname)
 *    değişen geçiş sorulur — satır, sayfalama, süzgeç, arama parametresi
 *    değişimi sorulmaz; kaydedilmemiş içerik yerinde soluk kalır.
 * 2. ⚠️ **EKRANIN KENDİ KAYITTAN SONRAKİ GEÇİŞİ SORULMAZ:** yeni kayıt açıp
 *    kendi adresine geçen ekran `serbestGecis(hedefYol)` ile O HEDEFİ serbest
 *    bırakır — serbestlik bir sonraki geçişe değil **adıyla verilen yola**
 *    aittir (yanlış yola gidilirse soru yine sorulur).
 * 3. ⚠️ **SEKME KAPATMA AYNI NOKTADAN:** `beforeunload` da burada kurulur;
 *    ekran ikinci bir tarayıcı uyarısı yazmaz.
 *
 * Soru sürerken içerik kaydolursa (ör. form doldurmanın otomatik kaydı biter)
 * korunacak bir şey kalmamıştır: geçiş kullanıcının istediği yere yapılır.
 */

interface Kayit {
  sayi: number;
  etiket: string;
}

interface Baglam {
  bildir: (anahtar: string, kayit: Kayit | null) => void;
  serbestBirak: (hedefYol: string) => void;
}

const KaydedilmemisBaglami = createContext<Baglam | null>(null);

function toplamSayi(kayitlar: ReadonlyMap<string, Kayit>): number {
  let toplam = 0;
  for (const kayit of kayitlar.values()) toplam += kayit.sayi;
  return toplam;
}

export function KaydedilmemisIcerikKorumasi({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [kayitlar, setKayitlar] = useState<ReadonlyMap<string, Kayit>>(() => new Map());
  // Engelleyici geçiş ANINDA okur: durum güncellemesi henüz çizilmemiş olsa da
  // en güncel sayıyı görmesi için kayıt defteri ref'te de tutulur.
  const kayitRef = useRef<ReadonlyMap<string, Kayit>>(kayitlar);
  const serbestYolRef = useRef<string | null>(null);

  const bildir = useCallback((anahtar: string, kayit: Kayit | null): void => {
    const onceki = kayitRef.current;
    const mevcut = onceki.get(anahtar);
    const ayni =
      kayit === null
        ? mevcut === undefined
        : mevcut !== undefined && mevcut.sayi === kayit.sayi && mevcut.etiket === kayit.etiket;
    if (ayni) return;
    const yeni = new Map(onceki);
    if (kayit === null) yeni.delete(anahtar);
    else yeni.set(anahtar, kayit);
    kayitRef.current = yeni;
    setKayitlar(yeni);
  }, []);

  const serbestBirak = useCallback((hedefYol: string): void => {
    serbestYolRef.current = hedefYol;
  }, []);

  const engelle = useCallback(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: { pathname: string };
      nextLocation: { pathname: string };
    }): boolean => {
      if (currentLocation.pathname === nextLocation.pathname) return false;
      if (serbestYolRef.current === nextLocation.pathname) {
        serbestYolRef.current = null;
        return false;
      }
      return toplamSayi(kayitRef.current) > 0;
    },
    [],
  );
  const blocker = useBlocker(engelle);

  const toplam = toplamSayi(kayitlar);

  useEffect(() => {
    if (toplam === 0) return;
    const uyar = (olay: BeforeUnloadEvent): void => {
      olay.preventDefault();
      olay.returnValue = '';
    };
    window.addEventListener('beforeunload', uyar);
    return () => window.removeEventListener('beforeunload', uyar);
  }, [toplam]);

  // Soru açıkken içerik kaydolduysa korunacak bir şey kalmadı — geçiş yapılır.
  useEffect(() => {
    if (blocker.state === 'blocked' && toplam === 0) blocker.proceed();
  }, [blocker, toplam]);

  const baglam = useMemo<Baglam>(() => ({ bildir, serbestBirak }), [bildir, serbestBirak]);
  const kalemler = [...kayitlar.entries()].filter(([, k]) => k.sayi > 0);

  return (
    <KaydedilmemisBaglami.Provider value={baglam}>
      {children}
      {/* Modal meşru: kayıt açmaz, bir onay sorusudur (P1). */}
      <Modal
        opened={blocker.state === 'blocked'}
        onClose={() => {
          if (blocker.state === 'blocked') blocker.reset();
        }}
        title="Kaydedilmemiş değişiklik"
      >
        <Stack data-testid="cikis-onayi">
          <Text size="sm">Bu sayfada kaydedilmemiş içerik var — ayrılırsanız kaybolur.</Text>
          <Stack gap={2}>
            {kalemler.map(([anahtar, k]) => (
              <Text key={anahtar} size="sm" fw={600} data-testid="cikis-onayi-kalem">
                {k.sayi} {k.etiket}
              </Text>
            ))}
          </Stack>
          <Group justify="flex-end">
            <Button
              variant="default"
              data-testid="cikis-sayfada-kal"
              onClick={() => {
                if (blocker.state === 'blocked') blocker.reset();
              }}
            >
              Sayfada kal
            </Button>
            <Button
              color={ANLAM.engel}
              data-testid="cikis-ayril"
              onClick={() => {
                if (blocker.state === 'blocked') blocker.proceed();
              }}
            >
              Değişiklikleri at ve ayrıl
            </Button>
          </Group>
        </Stack>
      </Modal>
    </KaydedilmemisBaglami.Provider>
  );
}

/**
 * Ekranın bildirimi: `sayi` kaydedilmemiş kalem (0 = yok), `etiket` onay
 * sorusunda sayının yanında görünen ad (*"madde satırı"* · *"künye"*).
 * Bileşen kalkınca kaydı kendiliğinden düşer.
 *
 * ⚠️ Koruma dışında (sağlayıcısız) çağrılırsa PATLAR — sessizce korumasız
 * kalan bir ekran, korumanın hiç olmamasından kötüdür (§2.40).
 */
export function useKaydedilmemisIcerik(
  sayi: number,
  etiket: string,
): { serbestGecis: (hedefYol: string) => void } {
  const baglam = useContext(KaydedilmemisBaglami);
  if (!baglam) {
    throw new Error('useKaydedilmemisIcerik, KaydedilmemisIcerikKorumasi dışında çağrıldı');
  }
  const anahtar = useId();
  const { bildir, serbestBirak } = baglam;
  useEffect(() => {
    bildir(anahtar, sayi > 0 ? { sayi, etiket } : null);
  }, [bildir, anahtar, sayi, etiket]);
  useEffect(() => () => bildir(anahtar, null), [bildir, anahtar]);
  return { serbestGecis: serbestBirak };
}
